import { useAccountApi } from '../../api/AccountApiProvider';
import { useAsync } from '../../api/useAsync';
import { useAccountSession } from '../../auth/AccountSessionProvider';
import type { CertificateReport, Resource, Tenant, TenantVersions } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Link } from '../../router/Router';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { CopyField } from '../../ui/CopyField';
import { ResourceTable } from '../../ui/ResourceTable';
import { Button, Card, Loading, PageHead, Stat } from '../../ui/primitives';
import { IdentityStateBadge, StatusBadge, TenantStateBadge } from '../../ui/StatusBadge';
import { IconExternal } from '../../ui/icons';
import { componentOf, readingOf, stepLabelKey, stepTone } from './rollout';
import { attemptsOf, certificateBlock, relayPublished } from './reconcileReport';

/**
 * The tenancy a customer's representative acts for.
 *
 * Built from the same three calls the operator's card is built from, and it
 * shows something else, because a customer is not running a fleet. What they
 * come for is on it: the addresses their people and their connectors dial, what
 * their cluster is running, and what is in its catalogue. What they never asked
 * to see is not: the two sets of names on a certificate, the anonymous budget,
 * which of the gateway's settings its process has read, the steps of the last
 * application. Those are the working of the provider, and a screen that showed
 * them to a customer would be handing over somebody else's diagnostics as if
 * they were the customer's job.
 *
 * One thing about the certificate does stay, and only when it is wrong now: a
 * tenancy with no certificate publishes no registration relay, which is why an
 * connector at the customer's own site will not come up. That is the customer's
 * problem the moment it is true, and they hear about it here rather than from
 * an engineer who cannot enrol a node.
 *
 * There is no button that applies the declared state: a representative does not
 * carry `reconcile_tenant`, and a control that is only ever refused is worse
 * than none.
 */
export function TenancyScreen() {
  const { t } = useI18n();
  const { session } = useAccountSession();

  const tenancy = session?.tenancy;
  if (tenancy === undefined) {
    return (
      <div className="content-inner">
        <Alert tone="warn" title={t('identityProvider.notSole.title')}>
          <p>{t('identityProvider.notSole.body')}</p>
        </Alert>
      </div>
    );
  }

  return <OneTenancy slug={tenancy.slug} />;
}

function OneTenancy({ slug }: { slug: string }) {
  const { t } = useI18n();
  const api = useAccountApi();

  const card = useAsync((signal) => api.provider.getTenant(slug, signal), `tenant:${slug}`);
  const board = useAsync((signal) => api.provider.readRollout(signal), 'rollout');
  const catalogue = useAsync((signal) => api.provider.listResources(slug, signal), `resources:${slug}`);

  if (card.status === 'loading') {
    return (
      <div className="content-inner">
        <Loading />
      </div>
    );
  }

  if (card.status === 'error') {
    return (
      <div className="content-inner">
        <ErrorAlert
          error={card.error}
          action={
            <div style={{ marginTop: 12 }}>
              <Button size="sm" onClick={card.reload}>
                {t('common.retry')}
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  const tenant = card.value;
  const line =
    board.status === 'ready'
      ? board.value.tenants.find((row) => row.slug === slug)
      : undefined;
  const certificate = certificateOf(tenant);

  return (
    <div className="content-inner">
      <PageHead
        title={tenant.display_name}
        sub={tenant.tenancy_domain}
        actions={
          <Link to={{ name: 'account.tenancyUsage' }} className="btn btn-ghost">
            {t('usage.title')}
          </Link>
        }
      />

      <div className="stack">
        <div className="grid g-4">
          <Stat
            label={t('tenant.field.state')}
            value={<TenantStateBadge state={tenant.state} />}
            {...(certificate?.not_after == null
              ? {}
              : { sub: t('tenancy.certificate.until', { when: certificate.not_after.slice(0, 10) }) })}
          />
          <VersionTile line={line} loading={board.status === 'loading'} />
          <IdentityTile tenant={tenant} />
          <ResourcesTile
            count={catalogue.status === 'ready' ? catalogue.value.length : undefined}
            loading={catalogue.status === 'loading'}
          />
        </div>

        <CertificateFinding certificate={certificate} />

        <Addresses tenant={tenant} />

        <Version line={line} loading={board.status === 'loading'} />

        <Catalogue tenant={tenant} resources={catalogue.status === 'ready' ? catalogue.value : []} />
      </div>
    </div>
  );
}

/** What the last application read off the disk, when there was one. */
function certificateOf(tenant: Tenant): CertificateReport | undefined {
  const block = certificateBlock(tenant.last_reconcile);
  return block.present ? block.value : undefined;
}

function VersionTile({ line, loading }: { line: TenantVersions | undefined; loading: boolean }) {
  const { t } = useI18n();

  if (line === undefined) {
    return (
      <Stat
        label={t('tenant.field.version')}
        absent={{ label: t(loading ? 'common.loading' : 'common.unread') }}
      />
    );
  }
  return <Stat label={t('tenant.field.version')} value={<span className="mono">{line.declared}</span>} />;
}

function IdentityTile({ tenant }: { tenant: Tenant }) {
  const { t } = useI18n();

  const identity = tenant.identity;
  if (identity === undefined) {
    return <Stat label={t('identityProvider.title')} absent={{ label: t('common.unread') }} />;
  }

  const kind = identity.applied?.kind;
  return (
    <Stat
      label={t('identityProvider.title')}
      value={<IdentityStateBadge state={identity.state} />}
      {...(kind === undefined
        ? {}
        : {
            sub: t(kind === 'provider' ? 'identityProvider.kind.provider' : 'identityProvider.kind.own'),
          })}
    />
  );
}

function ResourcesTile({ count, loading }: { count: number | undefined; loading: boolean }) {
  const { t, formatNumber } = useI18n();

  if (count === undefined) {
    return (
      <Stat
        label={t('tenancy.tile.resources')}
        absent={{ label: t(loading ? 'common.loading' : 'common.unread') }}
      />
    );
  }
  return <Stat label={t('tenancy.tile.resources')} value={formatNumber(count)} />;
}

/**
 * The one thing about the certificate a customer is told, and only while it is
 * true: with no certificate there is no registration relay, and a connector at
 * their own site has nowhere to spend its join token.
 */
function CertificateFinding({ certificate }: { certificate: CertificateReport | undefined }) {
  const { t, formatDateTime } = useI18n();

  if (certificate === undefined) return null;

  const attempts = attemptsOf(certificate);

  return (
    <>
      {!relayPublished(certificate) && (
        <Alert tone="warn" title={t('certificate.relay.title')}>
          <p>{t('certificate.relay.body')}</p>
        </Alert>
      )}
      {attempts.kind === 'waiting' && (
        <Alert tone="warn" title={t('certificate.attempts.waiting.title')}>
          <p>
            {t('certificate.attempts.waiting.body', {
              attempts: attempts.attempts,
              when: formatDateTime(attempts.retryAfter),
            })}
          </p>
        </Alert>
      )}
      {attempts.kind === 'stalled' && (
        <Alert tone="deny" title={t('certificate.attempts.stalled.title')}>
          <p>{t('certificate.attempts.stalled.body', { attempts: attempts.attempts })}</p>
        </Alert>
      )}
    </>
  );
}

/**
 * What this tenancy is dialled at, and the one string a first contact checks
 * that does not come from whoever answered.
 *
 * The most asked-for thing on the screen, and the reason it is copyable rather
 * than merely printed: these are read down a telephone to somebody standing at
 * a terminal.
 */
function Addresses({ tenant }: { tenant: Tenant }) {
  const { t } = useI18n();

  return (
    <Card>
      <div className="card-head">
        <span className="ct">{t('tenancy.addresses.title')}</span>
      </div>
      <div className="card-pad stack">
        <CopyField label={t('tenant.field.consoleDomain')} value={tenant.console_domain} />
        <CopyField label={t('tenant.field.gatewayDomain')} value={tenant.gateway_domain} />
        {tenant.transport_ca_fingerprint === null ? (
          <p className="muted">{t('tenant.fingerprint.none')}</p>
        ) : (
          <CopyField
            label={t('tenant.section.fingerprint')}
            value={tenant.transport_ca_fingerprint}
          />
        )}
      </div>
    </Card>
  );
}

/**
 * What this tenancy's own cluster is running: the tag it is declared with, the
 * two components that can be read, and when they were last read.
 *
 * Its own line off the board and nothing else from it. Which wave it is in and
 * what the rest of the fleet runs are the provider's business.
 */
function Version({ line, loading }: { line: TenantVersions | undefined; loading: boolean }) {
  const { t, formatDateTime } = useI18n();

  return (
    <Card>
      <div className="card-head">
        <span className="ct">{t('tenancy.version.title')}</span>
      </div>
      <div className="card-pad">
        {line === undefined ? (
          <p className="muted">{t(loading ? 'common.loading' : 'tenancy.version.untold')}</p>
        ) : (
          <>
            <dl className="dl">
              <dt>{t('rollout.col.declared')}</dt>
              <dd className="mono">{line.declared}</dd>
              <dt>{t('rollout.col.authority')}</dt>
              <dd className="mono">
                <Reading row={line} component="authority" />
              </dd>
              <dt>{t('rollout.col.proxy')}</dt>
              <dd className="mono">
                <Reading row={line} component="proxy" />
              </dd>
              <dt>{t('rollout.col.observed')}</dt>
              <dd>
                {line.observed_at === undefined
                  ? t('rollout.observed.never')
                  : formatDateTime(line.observed_at)}
              </dd>
            </dl>
            {line.step !== undefined && (
              <div className="row" style={{ marginTop: 14 }}>
                <StatusBadge tone={stepTone(line.step)} label={t(stepLabelKey(line.step))} />
                <span className="faint">
                  {t('rollout.move.from')}
                  <span className="mono"> {line.step.from}</span>
                </span>
                <span className="faint">{formatDateTime(line.step.changed_at)}</span>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/** One component's version, or the kind of silence there is instead. */
function Reading({ row, component }: { row: TenantVersions; component: string }) {
  const { t } = useI18n();

  const found = componentOf(row, component);
  if (found === undefined) {
    return <StatusBadge tone="neutral" label={t('rollout.component.unreadable')} />;
  }
  const reading = readingOf(found);
  if (reading.read) return <>{reading.version}</>;
  return (
    <StatusBadge
      tone="neutral"
      label={t(
        reading.readBy === undefined ? 'rollout.component.unreadable' : 'rollout.component.notRead',
      )}
      {...(reading.reason === undefined ? {} : { hint: reading.reason })}
    />
  );
}

/**
 * What this cluster serves, to look at — and where it is changed, which is not
 * here.
 *
 * A catalogue is written in the cluster's own console by somebody that
 * tenancy's own identity provider vouched for, so the change carries a name to
 * that tenancy's own audit trail. The link is the whole of the answer to "where
 * do I add a host".
 */
function Catalogue({ tenant, resources }: { tenant: Tenant; resources: Resource[] }) {
  const { t, plural } = useI18n();

  return (
    <Card>
      <div className="card-head">
        <span className="ct">{t('catalog.title')}</span>
        <span className="ca faint">{plural('catalog.count', resources.length)}</span>
      </div>
      <div className="card-pad">
        {resources.length === 0 ? (
          <p className="muted">{t('catalog.none.body')}</p>
        ) : (
          <ResourceTable resources={resources} />
        )}
        <div className="row" style={{ marginTop: 14 }}>
          <a className="btn btn-ghost btn-sm" href={`https://${tenant.console_domain}/`}>
            <IconExternal size={15} />
            {t('identityProvider.openConsole')}
          </a>
        </div>
      </div>
    </Card>
  );
}
